import { useEffect, useRef } from 'react';

const useWatch = <T>(
  value: T,
  callback: (newVal: T, oldVal: T) => void,
  option?: {
    immediate?: boolean;
    equalityFn?: (newVal: T, oldVal: T) => boolean;
  },
) => {
  const oldVal = useRef<T>(option?.immediate ? value : null);
  const hasRunOnce = useRef(false);
  const callbackRef = useRef(callback);
  const equalityFnRef = useRef(option?.equalityFn);
  const immediateRef = useRef(option?.immediate);

  callbackRef.current = callback;
  equalityFnRef.current = option?.equalityFn;

  useEffect(() => {
    const isFirstRun = !hasRunOnce.current;
    const shouldRunOnFirstRender = isFirstRun && immediateRef.current;
    const shouldRun = shouldRunOnFirstRender || !isFirstRun;

    if (!shouldRun) {
      oldVal.current = value;
      hasRunOnce.current = true;
      return;
    }

    const hasEqualityFn = typeof equalityFnRef.current === 'function';
    const valueIsEqual = shouldRunOnFirstRender
      ? false
      : hasEqualityFn && equalityFnRef.current?.(value, oldVal.current as T);

    if (!hasEqualityFn || !valueIsEqual) {
      callbackRef.current(value, oldVal.current as T);
    }

    oldVal.current = value;
    hasRunOnce.current = true;
  }, [value]);
};

export default useWatch;
